import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { formatAddress } from '../../../features/directions/directionsUtils';
import { getCompanyClientUrlSegment } from '../../../shared/clients/companyClientRouting';
import './AppointmentClientProfileWidget.css';

const formatPhoneNumber = (phone) => {
  const rawPhone = String(phone || '').trim();
  if (!rawPhone) return '';

  const digits = rawPhone.replace(/\D/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (normalizedDigits.length !== 10) {
    return rawPhone;
  }

  return `(${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6)}`;
};

const Icon = ({ path, viewBox = '0 0 24 24', className = '' }) => (
  <svg className={className} width="18" height="18" viewBox={viewBox} fill="currentColor" aria-hidden>
    <path d={path} />
  </svg>
);

const icons = {
  email: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  phone: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z',
  person:
    'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
  place:
    'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
};

const initialsFromName = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Sidebar client summary for appointment details (markup + CSS; no MUI).
 */
const AppointmentClientProfileWidget = ({
  clientDetails,
  appointmentData,
  clientId,
  clientProfileLoading = false,
  canGetDirections = false,
  onGetDirections
}) => {
  const location = useLocation();
  const fromClientName = `${clientDetails?.firstName || ''} ${clientDetails?.lastName || ''}`.trim();
  const displayName =
    fromClientName ||
    (appointmentData?.clientName || '').trim() ||
    (appointmentData?.customerInfo?.name || '').trim() ||
    'Client';

  const email =
    clientDetails?.email ||
    appointmentData?.clientEmail ||
    appointmentData?.customerInfo?.email ||
    '';
  const phoneRaw =
    clientDetails?.phone ||
    appointmentData?.clientPhone ||
    appointmentData?.customerInfo?.phone ||
    '';
  const phoneDisplay = formatPhoneNumber(phoneRaw);

  const addressObj =
    clientDetails?.address ||
    appointmentData?.clientAddress ||
    appointmentData?.customerInfo?.address ||
    null;
  const addressLine = formatAddress(addressObj);

  const emergency = clientDetails?.emergencyContact;
  const hasEmergency =
    emergency && (String(emergency.name || '').trim() || String(emergency.phone || '').trim());

  return (
    <aside className="acpw" aria-label="Client profile">
      <div className="acpw__card">
        <h2 className="acpw__title">Client</h2>

        {clientProfileLoading && clientId ? (
          <p className="acpw__loading">Loading profile…</p>
        ) : (
          <>
            <div className="acpw__header">
              <div className="acpw__avatar" aria-hidden>
                {clientDetails?.avatarUrl ? (
                  <img src={clientDetails.avatarUrl} alt="" className="acpw__avatar-img" />
                ) : (
                  <span className="acpw__avatar-initials">{initialsFromName(displayName)}</span>
                )}
              </div>
              <div className="acpw__header-text">
                <p className="acpw__name">{displayName}</p>
              </div>
            </div>

            {!clientId ? (
              <p className="acpw__note">This booking is not linked to a saved client profile.</p>
            ) : null}

            <ul className="acpw__list">
              <li className="acpw__row">
                <Icon path={icons.email} className="acpw__icon" />
                {email ? (
                  <a href={`mailto:${email}`} className="acpw__link">
                    {email}
                  </a>
                ) : (
                  <span className="acpw__muted">No email</span>
                )}
              </li>
              <li className="acpw__row">
                <Icon path={icons.phone} className="acpw__icon" />
                {phoneRaw ? (
                  <a href={`tel:${phoneRaw}`} className="acpw__link">
                    {phoneDisplay}
                  </a>
                ) : (
                  <span className="acpw__muted">No phone</span>
                )}
              </li>
              {addressLine ? (
                <li className="acpw__row acpw__row--multiline">
                  <Icon path={icons.place} className="acpw__icon acpw__icon--top" />
                  <div className="acpw__address-block">
                    <span className="acpw__text">{addressLine}</span>
                    {canGetDirections && onGetDirections ? (
                      <button type="button" className="acpw__directions-btn" onClick={onGetDirections}>
                        Get directions
                      </button>
                    ) : null}
                  </div>
                </li>
              ) : canGetDirections && onGetDirections ? (
                <li className="acpw__row">
                  <Icon path={icons.place} className="acpw__icon" />
                  <button type="button" className="acpw__directions-btn" onClick={onGetDirections}>
                    Get directions
                  </button>
                </li>
              ) : null}
            </ul>

            {hasEmergency ? (
              <div className="acpw__section">
                <h3 className="acpw__section-label">Emergency contact</h3>
                <ul className="acpw__list acpw__list--tight">
                  {emergency.name ? (
                    <li className="acpw__row">
                      <Icon path={icons.person} className="acpw__icon" />
                      <span className="acpw__text">{emergency.name}</span>
                    </li>
                  ) : null}
                  {emergency.phone ? (
                    <li className="acpw__row">
                      <Icon path={icons.phone} className="acpw__icon" />
                      <a href={`tel:${emergency.phone}`} className="acpw__link">
                        {formatPhoneNumber(emergency.phone)}
                      </a>
                    </li>
                  ) : null}
                  {emergency.relationship ? (
                    <li className="acpw__row acpw__row--plain">
                      <span className="acpw__muted">{emergency.relationship}</span>
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}

            {clientId ? (
              <div className="acpw__footer">
                <Link
                  to={`/app/overview/clients/profile/${getCompanyClientUrlSegment(clientDetails || { id: clientId })}`}
                  state={{ returnTo: `${location.pathname}${location.search}` }}
                  className="acpw__profile-link"
                >
                  View full profile
                </Link>
              </div>
            ) : null}
          </>
        )}
      </div>
    </aside>
  );
};

export default AppointmentClientProfileWidget;
